<template>
  <Modal class="address-forms-modal" ref="AddressFormsModalRef" @close="closeAddressModal">
    <div class="address-forms" :class="{ hidden: addresses?.length === 0 }">
      <div class="error-message" v-if="addresses?.length === 0">
        Please add at least one address to proceed. Click the + Add Address below
      </div>
      <address-form
        v-for="(address, index) in addresses"
        :key="`stip_${stip.id}_${index}`"
        ref="addressForms"
        :applicant="applicant"
        :stipulation="stip"
        :addresses="addresses"
        :previousAddresses="previousAddresses"
        :address="address"
        :index="index"
        :disabled="savingChanges"
        :loading="savingChanges"
        @onDelete="deleteAddress($event.address, $event.index)"
        @onError="openErrorModal"
        @closeDialogueModal="closeDialogueModal"
      />
      <div
        class="link-wrapper"
        :class="[{ isVisible: addresses.length === 0 }]"
        v-if="addresses.length === 0"
      >
        <a class="underline" @click="addAddress">+ Add Address</a>
      </div>
    </div>

    <template #footer>
      <div class="footer" v-if="addresses.length > 0">
        <div class="link-wrapper desktop xs-mobile">
          <a class="underline" @click="addAddress">+ Add Another Address</a>
        </div>
        <div class="button-wrapper">
          <next-button
            ref="submitButton"
            @click="handleSubmit"
            :disabled="addingAddresses || addresses.length === 0"
            :loading="savingChanges"
            text="Submit"
            class="default-btn submit-btn"
          />
        </div>
      </div>
    </template>
  </Modal>

  <teleport to="#modal-target">
    <custom-dialogue-modal
      ref="dialogueModalRef"
      v-if="showDialogueModal"
      :loading="dialogueModalOptions.loading"
      :options="dialogueModalOptions"
    />
  </teleport>
</template>

<script setup lang="ts">
import { useStore } from "vuex";
import { onMounted, ref, computed, watch, nextTick } from "vue";
import type { Stipulation } from "@/models/opal";
import NextButton from "@/components/shared/NextButton.vue";
import AddressForm from "@/components/forms/address-form/AddressForm.vue";
import Modal from "@/components/shared/Modal.vue";
import type { Applicant, Address, AddressFormInputs } from "@/models/application";
import { ButtonTypes } from "@/models/modal";
import { FormModes } from "@/models/form";
import useAddressForms from "@/composables/forms/useAddressForms";
import useArrayHelpers from "@/use/arrayHelpers";
import { resolveMergeFields } from "@/use/formatter";
import CustomDialogueModal from "@/components/modals/CustomDialogueModal.vue";
import useCustomDialogueModal from "@/composables/useCustomDialogueModal";
import { AddressTypeIds } from "@/config/constants";
import useApplicationData from "@/composables/useApplicationData";

const store = useStore();

interface Props {
  stip: Stipulation;
  applicant: Applicant;
  previousAddresses: Address[];
}

const props = withDefaults(defineProps<Props>(), {});

const { findDuplicatesWithCounts } = useArrayHelpers();
const { tollFreeNumber, hrefTFN, emailAddress } = useApplicationData();

const emit = defineEmits<{
  (e: "success", updatedStip: Stipulation): void;
  (e: "close"): void;
}>();

const addressForms = ref<InstanceType<typeof AddressForm>[] | null>();
const dialogueModalRef = ref<InstanceType<typeof Modal> | null>();

const { dialogueModalOptions, showDialogueModal, openDialogueModal, closeDialogueModal } =
  useCustomDialogueModal();

const addresses = ref<AddressFormInputs[]>([]);

const addingAddresses = ref<boolean>(false);
const savingChanges = ref<boolean>(false);

const mobileBreakpoint = 576;
const tabletBreakpoint = 992;

const windowWidth = computed(() => window.innerWidth);

const isMobile = computed<boolean>(() => windowWidth.value <= mobileBreakpoint);

const isTablet = computed<boolean>(() => windowWidth.value <= tabletBreakpoint);

const isDesktop = computed<boolean>(() => !isMobile.value && !isTablet.value);

const formattedAddresses = computed(() => {
  if ((addressForms.value || [])?.length > 0) {
    return addressForms.value?.map((f: any) => f.formattedAddress);
  }
  return [];
});

const { validateAddressForms, updateAddressMismatchStipAsync, errors } = useAddressForms({
  forms: addresses,
  stipulation: computed(() => props.stip),
  savingChanges,
});

function addAddress() {
  const newForm = {
    id: 0, // indicates it's a new entry
    addressId: 0,
    mode: FormModes.add,
    applicantId: props?.stip?.applicantId || props?.applicant?.id,
    address1: null,
    address2: null,
    addressTypeId: AddressTypeIds.PreviousAddress,
    addressTypeName: null,
    addressUnitTypeId: null,
    addressUnitTypeName: null,
    city: null,
    countryId: null,
    countryName: null,
    postalCode: null,
    stateId: null,
    stateName: null,
    stipulationId: props.stip.id,
  };

  addresses.value.push(newForm);
}

function resetDialogueModal() {
  dialogueModalOptions.value = {
    title: undefined,
    description: undefined,
    buttons: [],
    loading: false,
    closeBtn: false,
  };

  showDialogueModal.value = false;
}

function deleteAddress(address: AddressFormInputs, index: number) {
  if (address.id > 0) {
    return;
  }

  let description = "Are you sure you want to delete this address record?";

  if (addresses.value.length === 1) {
    description =
      "Are you sure you want to delete this address record? You must provide at least one address record.";
  }

  openDialogueModal({
    closeBtn: false,
    description: description,
    buttons: [
      {
        label: "Yes",
        type: Number(ButtonTypes.Confirm),
        onClick: async () => {
          addresses.value.splice(index, 1);

          await nextTick(() => {
            closeDialogueModal();
          });
        },
      },
      {
        label: "No",
        type: Number(ButtonTypes.Cancel),
        onClick: () => {
          closeDialogueModal();
        },
      },
    ],
  });
}

function getFormRef(address: AddressFormInputs, index: number) {
  return addressForms.value?.find(
    a => a?.address?.addressId === address?.addressId && a.props.index === index,
  );
}

function handleMatches(matches: any[]) {
  let html = "";

  if (matches && matches.length > 0) {
    matches.forEach((s: Record<number, any[]>) => {
      html += `<li style="font-size: 12px; font-weight: 400;"><span class="xhighlight">${s[0]} (${s[1]}) <span></li>`;
    });
  }

  const addresses = `<ul style="margin: 0; padding: 0; list-style: none;">${html}</ul>`;

  openDialogueModal({
    description: `<span style="color: red;">Duplicate record(s) detected</span>. <p>${addresses}</p>`,
    buttons: [
      {
        label: "Go Back and Edit",
        type: Number(ButtonTypes.Cancel),
        onClick: () => {
          showDialogueModal.value = false;
          savingChanges.value = false;
        },
      },
      {
        label: "Save Changes",
        type: Number(ButtonTypes.Confirm),
        onClick: async () => {
          savingChanges.value = true;
          await confirmAddresses();
        },
      },
    ],
    closeBtn: false,
  });
}

function handleMinimumAddressesNotMet() {
  if (addresses.value?.length === 0) {
    openDialogueModal({
      description: `You must add at least 1 address before submitting.`,
      buttons: [
        {
          label: "Ok",
          type: Number(ButtonTypes.Confirm),
          onClick: async () => {
            showDialogueModal.value = false;
            savingChanges.value = false;
          },
        },
      ],
    });

    return;
  }
}

async function confirmAddresses() {
  // close dialogue modal if it is open
  closeDialogueModal();

  let html = "";

  if (formattedAddresses.value && formattedAddresses.value.length > 0) {
    formattedAddresses.value.forEach((s: string, index: number) => {
      html += `<li style="font-size: 12px; font-weight: 400;"><span class="xhighlight">Address ${
        index + 1
      }:&nbsp;${s}<span></li>`;
    });
  }

  const addresses = `<ul style="margin: 0; padding: 0; list-style: none;">${html}</ul>`;

  openDialogueModal({
    description: `Please confirm your entries <p>${addresses}</p> Once you click "Submit", the changes will be submitted for review and you will no longer be able to modify these records.`,
    buttons: [
      {
        label: "Cancel",
        type: Number(ButtonTypes.Cancel),
        onClick: async () => {
          closeDialogueModal();
          savingChanges.value = false;
        },
      },
      {
        label: "Submit",
        type: Number(ButtonTypes.Confirm),
        onClick: async () => {
          await saveChanges();
        },
      },
    ],
  });
}

async function saveChanges() {
  dialogueModalOptions.value.loading = true;

  const response = await updateAddressMismatchStipAsync(); // need this before pushing changes

  const updatedStip = {
    ...props.stip,
    stipulationStatusId: response.results?.stipulationStatusId || props.stip.stipulationStatusId,
    stipulationStatusName:
      response.results?.stipulationStatusName || props.stip.stipulationStatusName,
  };

  if (response.success) {
    handleSuccess(updatedStip);
  } else {
    openErrorModal();
  }
}
async function handleValidationErrors(indexKeys: string[]) {
  if (indexKeys.length === 0) {
    return;
  }
  let html = "";

  indexKeys.forEach((s: string) => {
    html += `<li style="font-size: 12px; font-weight: 400;"><span class="xhighlight">${s}<span></li>`;
  });

  const addresses = `<ul style="margin: 0; padding: 0; list-style: none;">${html}</ul>`;

  openDialogueModal({
    description: `Please resolve the errors in the following entries <p style="color: red;">${addresses}</p>`,
    buttons: [
      {
        label: "Ok",
        type: Number(ButtonTypes.Cancel),
        onClick: async () => {
          closeDialogueModal();
          savingChanges.value = false;
        },
      },
    ],
  });
}

function openErrorModal() {
  dialogueModalOptions.value.loading = false;

  let error =
    "Your changes could not be saved. Please try again. If the issue persists, please contact our support team at {{phoneNumber}} or {{email}}";

  error = resolveMergeFields(error, {
    phoneNumber: `<a class="underline" style="text-decoration: underline;" :href="\`tel:${hrefTFN.value}\`">${tollFreeNumber.value}</a>`,
    email: `<a class="underline" style="text-decoration: underline;"  :href="\`mailto:${emailAddress}\`">${emailAddress.value}</a>`,
  });

  closeDialogueModal();

  openDialogueModal({
    description: error,
    closeBtn: true,
    buttons: [
      {
        label: "Ok",
        type: Number(ButtonTypes.Confirm),
        onClick: async () => {
          closeDialogueModal();
          savingChanges.value = false;
        },
      },
    ],
  });
}

function handleSuccess(response: Stipulation) {
  emit("success", response);
  dialogueModalOptions.value.loading = false;
  closeDialogueModal();
}

function handleUnsavedChanges(forms: AddressFormInputs[]) {
  if (forms.length === 0) {
    return;
  }
  let html = "";

  forms.forEach((s: AddressFormInputs, index: number) => {
    if (s.mode !== FormModes.view) {
      html += `<li style="font-size: 12px; font-weight: 400;"><span class="xhighlight">Address ${index + 1}<span></li>`;
    }
  });

  const addresses = `<ul style="margin: 0; padding: 0; list-style: none;">${html}</ul>`;

  openDialogueModal({
    description: `You have made changes that haven't been saved yet or open forms. Please review the following entries and click the "Save Address" button. <p>${addresses}</p>`,
    buttons: [
      {
        label: "Ok",
        type: Number(ButtonTypes.Cancel),
        onClick: async () => {
          closeDialogueModal();
          savingChanges.value = false;
        },
      },
    ],
  });
}
async function handleSubmit() {
  savingChanges.value = true;
  errors.value = [];

  try {
    if (addresses.value?.length === 0) {
      handleMinimumAddressesNotMet();
    }

    // check if any unsaved changes
    if (!addresses.value.every(a => a.mode === FormModes.view)) {
      handleUnsavedChanges(addresses.value);
      return;
    }
    addressForms.value?.forEach((element: InstanceType<typeof AddressForm>) => {
      element?.validate();
    });

    const validationResponse = await validateAddressForms();

    if (validationResponse === null) {
      // Not a validation error, something else went wrong
      openErrorModal();
      return;
    }

    if ((validationResponse.indexKeys || [])?.length > 0) {
      handleValidationErrors(validationResponse.indexKeys || []);
      return;
    }

    if (validationResponse.valid) {
      if (addresses.value.length >= 1) {
        const duplicates = findDuplicatesWithCounts(formattedAddresses.value || []);

        if (duplicates && duplicates.length > 0) {
          handleMatches(duplicates as any[]);
        } else {
          confirmAddresses();
        }
      }
    } else {
      savingChanges.value = false;
    }
  } catch {
    savingChanges.value = false;
  }
}

async function closeAddressModal() {
  openDialogueModal({
    closeBtn: false,
    description: `Address information will not be reviewed until you click "submit" button below. Are you sure you want to close this window without submitting?`,
    buttons: [
      {
        label: "Yes",
        type: Number(ButtonTypes.Confirm),
        onClick: async () => {
          addresses.value = addresses.value.filter((a: AddressFormInputs) => a.id > 0);

          emit("close");
          showDialogueModal.value = false;
          savingChanges.value = false;
        },
      },
      {
        label: "No",
        type: Number(ButtonTypes.Cancel),
        onClick: async () => {
          showDialogueModal.value = false;
          savingChanges.value = false;
        },
      },
    ],
  });
}

function initializeDialogueModal() {
  try {
    if (
      dialogueModalRef.value &&
      dialogueModalRef.value.modalHeader &&
      dialogueModalRef.value.modalBody &&
      dialogueModalRef.value.modalContainer
    ) {
      dialogueModalRef.value.modalHeader.style.height = "0";
      dialogueModalRef.value.modalContainer.style.maxWidth = "50%";
      dialogueModalRef.value.modalBody.style.minHeight = "100px";
      dialogueModalRef.value.modalContainer.classList.add("xs-mobile-w100 xs-text-center");
    }
  } catch {
    return;
  }
}

watch(
  () => dialogueModalRef.value,
  value => {
    if (value) {
      initializeDialogueModal();
    } else {
      resetDialogueModal();
    }
  },
);

onMounted(() => {
  props.previousAddresses.forEach((a: any) => {
    addresses.value.push({
      ...a,
      addressId: a.id,
      mode: FormModes.view,
      stipulationId: props?.stip?.id,
    });
  });

  if (addresses.value.length === 0) {
    addAddress();
  }
});

defineExpose({
  addresses,
  getFormRef,
});
</script>

<style scoped>
.sc-button {
  background-color: var(--theme-color, #000);
  color: #fff;
  font-family: "Montserrat";
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 20px;
  /* 142.857% */
  border-radius: 4px;
}

.default-button {
  background-color: #000;
  color: #fff;
  display: flex;
  max-width: 187px;
  height: 34px;
  padding: 11px 16px;
  justify-content: center;
  align-items: center;
  gap: 10px;
}

.button-wrapper button:is(.submit-btn) {
  max-width: 187px !important;
  min-width: 187px !important;
  height: 34px !important;
  padding: 11px 16px;
  font-size: 14px;
  font-style: normal;
  font-weight: 600 !important;
  line-height: 20px; /* 142.857% */
}

.button-wrapper button:is(.default-btn) {
  font-family: "Montserrat" !important;
  background-color: #000 !important;
  color: #fff !important;
}

.address-forms {
  padding: 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.address-forms .error-message {
  font-size: 14px;
  color: red;
  font-weight: 600;
}
.address-forms .error-message {
  text-align: center;
  border: 1px solid red;
  padding: 12px;
}

.link-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
}
.link-wrapper a.underline {
  text-decoration: underline;
}

.mobile:not(.isVisible) {
  display: none;
}

.link-wrapper.mobile .isVisible {
  display: flex;
  text-align: center !important;
}

/* Style the modal footer */
.address-forms-modal .footer {
  justify-content: space-between;
  flex-direction: row;
  width: 100%;
  display: flex;
}

.dialogue-modal-header.h1 {
  margin: 0;
  padding: 0.5rem 0 0 0;
}

.dialogue-modal-footer {
  display: flex;
  flex-direction: row;
}
@media (max-width: 992px) {
  .error-message {
    text-align: center;
  }
  .footer {
    justify-content: center;
    flex-wrap: wrap;
  }
  .address-forms-modal .footer {
    justify-content: space-between;
  }
  .dialogue-modal-content .footer button {
    min-width: 65px;
    max-width: 50%;
  }
}
@media (max-width: 576px) {
  .address-forms-modal .footer {
    justify-content: center;
  }
  .desktop {
    display: none;
  }
  .mobile {
    display: block;
  }
  .xs-mobile {
    display: flex;
  }
  .link-wrapper.xs-mobile {
    display: flex;
    min-width: 100%;
    justify-content: center;
    margin-bottom: 20px;
  }
  .link-wrapper.mobile {
    text-align: center;
  }
  .button-wrapper button:is(.submit-btn) {
    max-width: 100% !important;
  }
}
</style>
